import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import text from 'styles/utility/text.module.scss'
import typography from 'styles/components/typography.module.scss'
import modal from 'styles/components/modal.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStaff, getStaffOpen } from 'reducks/modal/selectors'
import { closeStaffModal, openCautionModal } from 'reducks/modal/operations'
import { Card, CardInner, ReceiveCard } from 'components/Card'
import { Spacer } from 'components/Utility'
import { FormLabel } from 'components/Label'
import { ExternalLink } from 'components/Link'
import { SubDescription } from 'components/Typography'
import { BlueButton, CautionButton, GreyButton } from 'components/Button'
import { inviteStaff, removeStaffFromStore, saveStaff } from 'reducks/staff/operations'
import { db, FirebaseDocRefType } from 'firebase/index'
import { getIsAdmin, getRoles, getStoreRef, getSwitchingStoreRefs } from 'reducks/auth/selectors'
import { Checkbox, SelectBox } from 'components/Form'
import { useManagedStores } from 'hooks/store'
import { ManagedStoreType } from 'types/store'
import { getStores } from 'reducks/store/selectors'
import { staffRoleOptions } from 'constants/options'
import { useCheckRef, useSelectNumber } from 'hooks/form'
import { FlexRow } from 'components/Flex'
import { getStaffs } from 'reducks/staff/selectors'
import { RoleType } from 'types/staff'

const MemberModal: FC = () => {
  const dispatch = useDispatch()
  const isAdmin = useSelector((state: State) => getIsAdmin(state))
  const myRoles = useSelector((state: State) => getRoles(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const switchingStoreRefs = useSelector((state: State) => getSwitchingStoreRefs(state))
  const stores = useSelector((state: State) => getStores(state))
  const open = useSelector((state: State) => getStaffOpen(state))
  const staff = useSelector((state: State) => getStaff(state))
  const staffs = useSelector((state: State) => getStaffs(state))

  const [role, setRole] = useState<number>(0)
  const [isInvitationPending, setInvitationPending] = useState<boolean>(false)
  const [storeRefs, setStoreRefs] = useState<FirebaseDocRefType[]>([])

  const handleStoreRefs = useCheckRef(setStoreRefs, db.collection('stores'))
  const selectRole = useSelectNumber(setRole)
  const closeModal = useCallback(() => dispatch(closeStaffModal()), [dispatch])
  const openDeleteConfirm = useCallback(() => {
    dispatch(
      openCautionModal(
        '削除する',
        'キャンセル',
        '削除したメンバーは元に戻すことができません。本当によろしいですか？',
        () => dispatch(removeStaffFromStore(staff, storeRef)),
        'メンバーを削除します'
      )
    )
  }, [staff])

  // ユーザーの権限状態
  const isAdminViewingStore = isAdmin && typeof storeRef !== 'undefined'
  const managedStores: ManagedStoreType[] = useManagedStores(switchingStoreRefs, stores, isAdminViewingStore, staffs)
  // 権限を変更できるのは、操作者が管理者の店舗だけ（スーパー管理者は全店舗）
  const editableStores: ManagedStoreType[] = useMemo(
    () =>
      isAdmin
        ? managedStores
        : managedStores.filter((managedStore) =>
            myRoles.some((myRole: RoleType) => myRole.store_ref.id === managedStore.store_ref.id && myRole.role === 9)
          ),
    [isAdmin, managedStores, myRoles]
  )

  useEffect(() => {
    if (!staff) return
    // 権限を与えられている店舗を取得
    const _storeRefs: FirebaseDocRefType[] = []
    for (let i = 0; i < staff.roles.length; i = (i + 1) | 0) {
      const role = staff.roles[i]
      if (editableStores.every((editableStore) => editableStore.store_ref.id !== role.store_ref.id)) continue
      _storeRefs.push(role.store_ref)
    }
    setStoreRefs([..._storeRefs])
    // 招待中（期限切れを含む）であれば、招待リンクを再送できるようにする
    setInvitationPending(!!staff.invitation_expired_at)
    // 既存の権限を取得
    const role = staff.roles[0]
    if (!role) return
    setRole(role.role)
  }, [staff, editableStores])

  return (
    <div className={open ? modal.open : ''}>
      <div className={modal.overlay}>
        <div className={modal.overlay__background} onClick={closeModal} />
        {open && (
          <div className={modal.overlay__container}>
            <Card>
              <CardInner>
                <h2 className={`${text.font_large} ${text.color_black_sacri} ${text.font_bold}`}>
                  メンバーの権限を変更します
                </h2>
                <Spacer size={12} />
                <FormLabel label={'メールアドレス'} required={false} />
                <Spacer size={12} />
                <p className={`${text.font_large} ${text.font_bold} ${text.wordBreakAll}`}>{staff.email}</p>
                <Spacer size={12} />
                <FormLabel label={'権限'} required={true} />
                <Spacer size={8} />
                <p className={typography.description_sub}>
                  管理者は全ての操作を行うことができます。ビューワーは、お客さまの注文確認や在庫変更ができますが、新規の商品登録はできません。詳しくは
                  <ExternalLink
                    label={'こちらの記事'}
                    url={'https://sacrisupport.zendesk.com/hc/ja/articles/4403860286745'}
                  />
                  を参考にしてください。
                </p>
                <Spacer size={8} />
                <SelectBox options={staffRoleOptions} select={selectRole} value={role} />
                <Spacer size={12} />
                <FormLabel label={'アクセスできる店舗を選択する'} required={true} />
                <Spacer size={8} />
                <SubDescription description={'アクセスを許可する店舗にチェックを入れてください。'} />
                <Spacer size={8} />
                {editableStores.map((editableStore) => {
                  return (
                    <React.Fragment key={editableStore.store_ref.id}>
                      <Checkbox
                        id={editableStore.store_ref.id}
                        isChecked={storeRefs.some((storeRef) => storeRef.id === editableStore.store_ref.id)}
                        label={editableStore.store_name}
                        onChange={handleStoreRefs}
                      />
                      <Spacer size={8} />
                    </React.Fragment>
                  )
                })}
                {isAdmin && (
                  // スーパー管理者は対象メンバーの操作中店舗に対する通知設定を変更できる
                  <>
                    <Spacer size={16} />
                    <ReceiveCard staff={staff} />
                  </>
                )}
                <Spacer size={32} />
                <BlueButton
                  disabled={isInvitationPending && storeRefs.length === 0}
                  label={isInvitationPending ? '招待リンクを再送する' : '保存する'}
                  onClick={() => {
                    isInvitationPending
                      ? dispatch(inviteStaff(staff.email, role, storeRefs, true))
                      : dispatch(
                          saveStaff(
                            staff,
                            role,
                            storeRefs,
                            editableStores.map((editableStore) => editableStore.store_ref)
                          )
                        )
                    closeModal()
                  }}
                  size={'medium'}
                  type={'main'}
                />
                <Spacer size={24} />
                <FlexRow justifyContent={'center'}>
                  <GreyButton label={'キャンセル'} onClick={closeModal} size={'small'} type={'outlined'} />
                  <Spacer size={24} axis={'horizontal'} />
                  <CautionButton
                    disabled={staff.roles.every((role: RoleType) => role.store_ref.id !== storeRef.id)}
                    label={'削除する'}
                    onClick={openDeleteConfirm}
                    size={'small'}
                  />
                </FlexRow>
              </CardInner>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default MemberModal
